/**
 * Persistência do FCM registration token deste device no `users/{uid}`.
 *
 * Estratégia multi-device:
 *  - Cada device emite um token diferente. Guardamos vários (cap 5).
 *  - Dedup é por valor de token (não por device id — não temos).
 *  - Quando passa do cap, dropa o mais antigo. Tokens "antigos" são os
 *    primeiros do array — a ordem é preservada pelo `arrayUnion`.
 *
 * Rotação:
 *  - FCM eventualmente invalida tokens. O server-side `sendPush` lida com
 *    cleanup (remove tokens `messaging/registration-token-not-registered`).
 */

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  type Firestore,
} from "firebase/firestore";
import { db } from "../../lib/firebase/config";
import { FCM_TOKENS_MAX } from "../../shared/notifications";

/** Lê tokens atuais + faz upsert do `token` (dedup + cap). */
export async function registerFcmToken(
  uid: string,
  token: string,
  firestore: Firestore = db,
): Promise<void> {
  if (!uid || !token) return;
  const ref = doc(firestore, "users", uid);
  const snap = await getDoc(ref);
  const current = (snap.exists() && Array.isArray(snap.data().fcmTokens)) ?
    (snap.data().fcmTokens as string[]) :
    [];
  const next = mergeTokens(current, token);
  if (arraysEqual(current, next) && snap.data()?.notificationsOptIn === true) return;
  await setDoc(
    ref,
    {
      fcmTokens: next,
      notificationsOptIn: true,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/** Remove um token específico (logout / opt-out). */
export async function unregisterFcmToken(
  uid: string,
  token: string,
  firestore: Firestore = db,
): Promise<void> {
  if (!uid || !token) return;
  const ref = doc(firestore, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const current = Array.isArray(snap.data().fcmTokens) ? (snap.data().fcmTokens as string[]) : [];
  const next = current.filter((t) => t !== token);
  if (next.length === current.length) return;
  await setDoc(ref, { fcmTokens: next, updatedAt: serverTimestamp() }, { merge: true });
}

/**
 * Opt-out do usuário (toggle de Settings): desliga `notificationsOptIn` —
 * que é o campo que TODOS os crons de push filtram — e remove o token deste
 * device, numa escrita só. Tokens de outros devices ficam: se o usuário
 * religar, os aparelhos antigos voltam a valer sem re-permissão.
 */
export async function optOutNotifications(
  uid: string,
  token: string | null,
  firestore: Firestore = db,
): Promise<void> {
  if (!uid) return;
  const ref = doc(firestore, "users", uid);
  const snap = await getDoc(ref);
  const current = (snap.exists() && Array.isArray(snap.data().fcmTokens)) ?
    (snap.data().fcmTokens as string[]) :
    [];
  const next = token ? current.filter((t) => t !== token) : current;
  await setDoc(
    ref,
    {
      fcmTokens: next,
      notificationsOptIn: false,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * Dedup + cap. Exportado para testar a lógica pura sem mock de Firestore.
 *
 *  - Se `token` já está em `existing`, devolve `existing` inalterado.
 *  - Caso contrário, anexa e trunca para `FCM_TOKENS_MAX` mantendo os mais
 *    recentes (drop dos primeiros = mais antigos).
 */
export function mergeTokens(existing: string[], token: string): string[] {
  if (existing.includes(token)) return existing;
  const next = [...existing, token];
  if (next.length <= FCM_TOKENS_MAX) return next;
  return next.slice(next.length - FCM_TOKENS_MAX);
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
